import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { usePlanillonStore } from '../store';
import { TIPOS_PLANILLA, initialPlanilla, type PlanillaCabecera, type PlanillaFormErrors } from '../types/planillon';
import { MESES } from '../../periodos/types/periodo';

interface Props {
  open: boolean;
  onClose: () => void;
}

const EditPlanilla: React.FC<Props> = ({ open, onClose }) => {
  const { selectedPeriodoId, periodos, createPlanilla, loading } = usePlanillonStore();

  const [formData, setFormData] = useState<PlanillaCabecera>({
    ...initialPlanilla,
    idPeriodo: selectedPeriodoId || 0,
  });
  const [errors, setErrors] = useState<PlanillaFormErrors>({});

  const handleChange = (field: keyof PlanillaCabecera, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof PlanillaFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: PlanillaFormErrors = {};
    if (!formData.idPeriodo) newErrors.idPeriodo = 'Seleccione un periodo';
    if (!formData.descripcion.trim()) newErrors.descripcion = 'La descripción es requerida';
    if (!formData.tipoPlanilla) newErrors.tipoPlanilla = 'Seleccione un tipo';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      await createPlanilla(formData);
      onClose();
      setFormData({ ...initialPlanilla, idPeriodo: selectedPeriodoId || 0 });
    } catch {
      // Error handled in store
    }
  };

  const periodoSeleccionado = periodos.find((p) => p.idPeriodo === (formData.idPeriodo || selectedPeriodoId));
  const mesLabel = periodoSeleccionado
    ? `${MESES.find((m) => m.value === periodoSeleccionado.mes)?.label} ${periodoSeleccionado.anio}`
    : '';

  // Auto-generate description when periodo changes
  const handlePeriodoChange = (idPeriodo: number) => {
    const per = periodos.find((p) => p.idPeriodo === idPeriodo);
    const mesName = per ? MESES.find((m) => m.value === per.mes)?.label : '';
    const desc = per ? `Planilla ${formData.tipoPlanilla.toLowerCase()} - ${mesName} ${per.anio}` : '';
    setFormData((prev) => ({ ...prev, idPeriodo, descripcion: desc }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nueva Planilla</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <FormControl fullWidth error={!!errors.idPeriodo} sx={{ mt: 1 }}>
          <InputLabel>Periodo</InputLabel>
          <Select
            value={formData.idPeriodo || ''}
            label="Periodo"
            onChange={(e) => handlePeriodoChange(Number(e.target.value))}
          >
            {periodos
              .filter((p) => p.estado === 'ABIERTO')
              .map((p) => {
                const mes = MESES.find((m) => m.value === p.mes);
                return (
                  <MenuItem key={p.idPeriodo} value={p.idPeriodo}>
                    {mes?.label} {p.anio}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>

        <FormControl fullWidth error={!!errors.tipoPlanilla}>
          <InputLabel>Tipo de Planilla</InputLabel>
          <Select
            value={formData.tipoPlanilla}
            label="Tipo de Planilla"
            onChange={(e) => {
              handleChange('tipoPlanilla', e.target.value);
              // Re-generate description
              const per = periodos.find((p) => p.idPeriodo === formData.idPeriodo);
              if (per) {
                const mesName = MESES.find((m) => m.value === per.mes)?.label;
                setFormData((prev) => ({
                  ...prev,
                  tipoPlanilla: e.target.value,
                  descripcion: `Planilla ${e.target.value.toLowerCase()} - ${mesName} ${per.anio}`,
                }));
              }
            }}
          >
            {TIPOS_PLANILLA.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Descripción"
          value={formData.descripcion}
          onChange={(e) => handleChange('descripcion', e.target.value)}
          error={!!errors.descripcion}
          helperText={errors.descripcion || (mesLabel ? `Periodo: ${mesLabel}` : '')}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          Crear
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPlanilla;
